/**
 * What the picker does with a REJECTED plan switch — and, the part that costs money if it is wrong,
 * whether the action's Stripe idempotency token may be rotated.
 *
 * The codes come from `switchFailureCode` in pages/api/premium/switch-plan.ts, which classifies the
 * service's message because the RPC surfaces `FAILED_PRECONDITION` for several distinct preconditions.
 * This is the other half of that contract: one place that says what each code MEANS to the buyer, so
 * a new code cannot quietly land in the generic "could not switch plan" bucket — which is the state
 * this replaced, where a declined card, a charge still in flight and a 23-hour lockout all rendered
 * as the same sentence.
 *
 * Pure and import-free so it can be unit-tested without dragging the app's dependency graph in (see
 * the orion-web CLAUDE.md note on jest and common-lib's ESM chain).
 */

/** What to tell the buyer, and what the picker does next. */
export type SwitchRejectionAction =
  /**
   * The upgrade DEFINITIVELY failed and Stripe is provably back where it started — nothing charged,
   * subscriber untouched. Worth its own message, because the generic one made this read as our fault
   * and sent buyers back to the same card.
   *
   * ⚠️ But do NOT render it as "your card was declined". `StripeClient.PaymentDeclinedException` says
   * so itself: the phrase is a token-release SIGNAL, "not a claim about the card". Two of its four
   * throw sites are OUR failures with a perfectly good card — a held invoice voided after a missing
   * confirmation secret, and a failed metadata stamp. Naming the card there sends someone to fetch a
   * second one for a problem on our side. The copy has to be true at all four.
   */
  | 'declined'
  /** An attempt of THIS switch is still running or already done; money may be moving. Poll, never error. */
  | 'in_flight'
  /** An EARLIER held upgrade blocks every action until it is finished or canceled. Offer the way out. */
  | 'blocked_by_hold'
  /** Indeterminate. One generic error, and the token is kept so a retry dedupes at Stripe. */
  | 'failed';

export interface SwitchRejectionPlan {
  action: SwitchRejectionAction;
  /**
   * Whether the action is DEFINITIVELY over, so the next attempt must mint a fresh token.
   *
   * True for exactly one code. Stripe replays a cached response for 24h, so reusing the token after a
   * decline would answer the corrected card with the old decline; reusing it after anything else is
   * what makes a double-submit charge once. Widening this is how a retry becomes a second charge.
   */
  releasesActionKey: boolean;
}

export function planSwitchRejection(code: string | undefined): SwitchRejectionPlan {
  switch (code) {
    case 'payment_declined':
      return { action: 'declined', releasesActionKey: true };
    case 'already_in_progress':
      return { action: 'in_flight', releasesActionKey: false };
    case 'pending_upgrade':
      return { action: 'blocked_by_hold', releasesActionKey: false };
    default:
      return { action: 'failed', releasesActionKey: false };
  }
}

/** What the picker does once a rejection is classified. One call per outcome, at most one of them. */
export interface SwitchRejectionHandlers {
  /** Mint a fresh token for the next attempt. Called for `declined` and nothing else. */
  releaseActionKey: () => void;
  showDeclined: () => void;
  /** Money may be moving: the "finishing up" notice plus a poll, never an error. */
  reflectInFlight: () => void;
  /** Open the discard-and-retry escape. Only reachable with a lane to aim the void at. */
  offerHoldEscape: () => void;
  showGenericFailure: () => void;
}

/**
 * Perform a classified rejection. Separate from `planSwitchRejection` on purpose: the table alone was
 * testable, but the WIRING that reads it was not, and the wiring is where the money is. Inverting the
 * release condition at the call site — `if (!plan.releasesActionKey)` — answers every corrected card
 * with Stripe's 24h-cached decline AND rotates the token on indeterminate failures, turning a retry
 * into a second charge. That edit used to pass the entire suite; now it fails here.
 *
 * `canOfferHoldEscape` is the caller's answer to "do I know which Stripe account to void against". No
 * lane, no escape — a click that can only report failure is worse than the plain error.
 */
export function applySwitchRejection(
  code: string | undefined,
  canOfferHoldEscape: boolean,
  handlers: SwitchRejectionHandlers,
): SwitchRejectionAction {
  const plan = planSwitchRejection(code);
  if (plan.releasesActionKey) handlers.releaseActionKey();
  if (plan.action === 'declined') {
    handlers.showDeclined();
    return plan.action;
  }
  if (plan.action === 'in_flight') {
    handlers.reflectInFlight();
    return plan.action;
  }
  if (plan.action === 'blocked_by_hold' && canOfferHoldEscape) {
    handlers.offerHoldEscape();
    return plan.action;
  }
  handlers.showGenericFailure();
  return 'failed';
}
